import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AlertTriangle, Ticket, Plane, FileWarning, Clock, Bug, ClipboardList } from "lucide-react";

interface Counts {
  openTickets: number;
  pendingPto: number;
  expiringDocs: number;
  openLongClockIns: number;
  newBugs: number;
  reviewsDue: number;
}

export function AdminAttention() {
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    let alive = true;
    void load().then(c => { if (alive) setCounts(c); });
    const t = setInterval(() => { void load().then(c => { if (alive) setCounts(c); }); }, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  async function load(): Promise<Counts> {
    const twelveHoursAgo = new Date(Date.now() - 12 * 3600 * 1000).toISOString();
    const thirtyDaysOut = new Date(Date.now() + 30 * 86400000).toISOString();
    const reviewSoon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const [
      openTickets, pendingPto, expiringDocs, openLong, newBugs, reviewsDue,
    ] = await Promise.all([
      supabase.from("hq_support_tickets").select("id", { count: "exact", head: true })
        .in("status", ["open", "in_progress"]),
      supabase.from("hq_pto_requests").select("id", { count: "exact", head: true })
        .eq("request_status", "pending"),
      supabase.from("hq_employee_documents").select("id", { count: "exact", head: true })
        .not("expires_at", "is", null).lt("expires_at", thirtyDaysOut),
      supabase.from("hq_time_logs").select("id", { count: "exact", head: true })
        .is("clock_out_time", null).lt("clock_in_time", twelveHoursAgo),
      supabase.from("hq_bug_reports").select("id", { count: "exact", head: true })
        .eq("status", "new"),
      supabase.from("hq_employees").select("id", { count: "exact", head: true })
        .eq("status", "active").neq("review_cycle", "none")
        .not("next_review_due_date", "is", null).lte("next_review_due_date", reviewSoon),
    ]);
    return {
      openTickets: openTickets.count ?? 0,
      pendingPto: pendingPto.count ?? 0,
      expiringDocs: expiringDocs.count ?? 0,
      openLongClockIns: openLong.count ?? 0,
      newBugs: newBugs.count ?? 0,
      reviewsDue: reviewsDue.count ?? 0,
    };
  }

  if (!counts) return null;
  const total = counts.openTickets + counts.pendingPto + counts.expiringDocs + counts.openLongClockIns + counts.newBugs + counts.reviewsDue;
  if (total === 0) {
    return (
      <div className="card border-ok-bg bg-ok-bg/40">
        <div className="flex items-center gap-2 text-sm font-medium text-ok">
          ✓ Nothing demands your attention right now.
        </div>
      </div>
    );
  }

  return (
    <div className="card border-orange-200 bg-orange-50">
      <div className="mb-3 flex items-center gap-2 text-orange-900">
        <AlertTriangle size={18} />
        <h2 className="font-semibold">Needs your attention</h2>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {counts.openTickets > 0 && (
          <AttentionItem to="/support" icon={<Ticket size={16} />} label="Open tickets" count={counts.openTickets} />
        )}
        {counts.pendingPto > 0 && (
          <AttentionItem to="/admin/pto" icon={<Plane size={16} />} label="Pending time off" count={counts.pendingPto} />
        )}
        {counts.openLongClockIns > 0 && (
          <AttentionItem to="/admin/time-payroll" icon={<Clock size={16} />} label="Open clock-ins >12h" count={counts.openLongClockIns} />
        )}
        {counts.expiringDocs > 0 && (
          <AttentionItem to="/admin/employees" icon={<FileWarning size={16} />} label="Docs expiring <30d" count={counts.expiringDocs} />
        )}
        {counts.newBugs > 0 && (
          <AttentionItem to="/admin/bugs" icon={<Bug size={16} />} label="New bug reports" count={counts.newBugs} />
        )}
        {counts.reviewsDue > 0 && (
          <AttentionItem to="/admin/reviews" icon={<ClipboardList size={16} />} label="Reviews due soon" count={counts.reviewsDue} />
        )}
      </div>
    </div>
  );
}

function AttentionItem({ to, icon, label, count }: { to: string; icon: React.ReactNode; label: string; count: number }) {
  return (
    <Link to={to} className="flex items-center justify-between rounded-lg border border-orange-200 bg-white px-3 py-2 transition hover:bg-orange-100">
      <span className="flex items-center gap-2 text-sm text-charcoal">{icon} {label}</span>
      <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white">{count}</span>
    </Link>
  );
}
